import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  MapPin, 
  Star, 
  Clock, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Calendar,
  Thermometer,
  Droplets,
  Zap,
  Activity,
  Heart,
  Sparkles,
  Target
} from "lucide-react";
import { Link } from "react-router-dom";

const ForBusinessV2 = () => {
  const features = [
    {
      icon: Calendar,
      title: "Easy Booking System",
      description: "Streamlined appointment scheduling that customers love"
    },
    {
      icon: Users,
      title: "Customer Management",
      description: "Track client preferences and booking history"
    },
    {
      icon: Target,
      title: "Analytics Dashboard",
      description: "Monitor your business performance and growth"
    },
    {
      icon: Heart,
      title: "Wellness Focus",
      description: "Built specifically for wellness and recovery services"
    }
  ];

  const services = [
    { icon: Thermometer, name: "Sauna", color: "bg-orange-100 text-orange-700" },
    { icon: Droplets, name: "Ice Bath", color: "bg-blue-100 text-blue-700" },
    { icon: Zap, name: "Cryotherapy", color: "bg-purple-100 text-purple-700" },
    { icon: Activity, name: "Red Light", color: "bg-red-100 text-red-700" },
    { icon: Sparkles, name: "Massage", color: "bg-green-100 text-green-700" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/lovable-uploads/c9d4d449-ac7c-4868-af9e-bf9b48c9faad.png" 
                alt="WellSuites" 
                className="h-8 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/explore" className="text-gray-600 hover:text-gray-900 font-medium">Explore</Link>
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900 font-medium">Pricing</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 font-medium">Contact</Link>
            </nav>
            <div className="flex items-center space-x-4">
              <Link to="/auth">
                <Button variant="ghost" className="text-gray-600 hover:text-gray-900">
                  Sign In
                </Button>
              </Link>
              <Link to="/auth">
                <Button className="bg-slate-800 hover:bg-slate-900 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-50 text-blue-700 hover:bg-blue-100">
              For Wellness Businesses
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Grow Your Wellness
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Business
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The complete platform for wellness and recovery businesses. Manage bookings, 
              track clients, and grow your business with our purpose-built tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="px-8 py-6 text-lg">
                Watch Demo
              </Button>
            </div>
          </div>

          {/* Hero Image/Dashboard Preview */}
          <div className="relative">
            <Card className="bg-white shadow-2xl border-0 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-slate-100 to-slate-200 h-96 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Activity className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Dashboard Preview</h3>
                    <p className="text-gray-600">Coming soon - Beautiful analytics and management tools</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfect for All Wellness Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Whether you offer recovery treatments or wellness therapies, our platform supports your business
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className={`${service.color} px-6 py-3 rounded-full flex items-center space-x-2`}>
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{service.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed specifically for wellness and recovery businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-lg">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of wellness businesses already using WellSuites to grow their operations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg">
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src="/lovable-uploads/c9d4d449-ac7c-4868-af9e-bf9b48c9faad.png" 
                  alt="WellSuites" 
                  className="h-8 w-auto filter brightness-0 invert"
                />
                <button className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2">
                  <img 
                    src="/lovable-uploads/b3af523e-f22c-4fe2-9969-5c45dc7caf3a.png" 
                    alt="Apple" 
                    className="h-4 w-4"
                  />
                  Get the app
                </button>
              </div>
              <p className="text-gray-400">
                The complete platform for wellness businesses
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Explore</h3>
              <div className="space-y-2">
                <a href="#" className="block text-gray-400 hover:text-white">Find Suites</a>
                <a href="#" className="block text-gray-400 hover:text-white">My Account</a>
                <Link to="/for-business-v2" className="block text-gray-400 hover:text-white">For Business</Link>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <div className="space-y-2">
                <a href="#" className="block text-gray-400 hover:text-white">About us</a>
                <Link to="/contact" className="block text-gray-400 hover:text-white">Contact us</Link>
                <Link to="/privacy" className="block text-gray-400 hover:text-white">Privacy policy</Link>
                <Link to="/terms" className="block text-gray-400 hover:text-white">Terms of Service</Link>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <div className="space-y-2">
                <a href="#" className="block text-gray-400 hover:text-white">Help Center</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2025 WellSuites. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ForBusinessV2;
